// ST-32 -Claude- Security and activity log.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { isNearrmeAdminRole, listAuditLogs } from '../api';
import { useAdminAuth } from '../useAdminAuth';

export function AdminLogsPage() {
  const { user } = useAdminAuth();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sessionUserId, setSessionUserId] = useState<string>('');

  const logs = useQuery({
    queryKey: ['admin', 'logs', date, isNearrmeAdmin ? sessionUserId : 'own'],
    queryFn: () => listAuditLogs(isNearrmeAdmin && sessionUserId ? Number(sessionUserId) : undefined, date || undefined),
  });

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Activity log</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        {isNearrmeAdmin
          ? 'Security and access events across the Nearrme business portal.'
          : 'Your own security and access events in the Nearrme business portal.'}
      </p>

      <Card padded className="mt-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
            />
          </div>
          {isNearrmeAdmin && (
            <div>
              <label htmlFor="activity-session-user-id" className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Session user ID</label>
              <input
                id="activity-session-user-id"
                type="number"
                value={sessionUserId}
                onChange={(e) => setSessionUserId(e.target.value)}
                placeholder="Optional"
                className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm w-32"
              />
            </div>
          )}
          <Button variant="primary" size="md" onClick={() => logs.refetch()}>
            Refresh
          </Button>
        </div>
      </Card>

      {logs.isLoading && (
        <div className="mt-4 space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <LoadingSkeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {logs.error && (
        <Card padded className="mt-4">
          <div className="text-red-600 dark:text-red-400 text-sm">{(logs.error as Error).message}</div>
        </Card>
      )}

      {logs.data && (
        <>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {logs.data.length} {logs.data.length === 1 ? 'entry' : 'entries'}
          </div>
          {logs.data.length === 0 ? (
            <EmptyState title="No log entries" description="No activity log entries match these filters." />
          ) : (
            <Card padded={false} className="mt-2 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                    <tr className="text-left">
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Time</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Event</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">User</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Role</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Scope</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">IP</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {logs.data.map((l) => (
                      <tr key={l.id} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                        <td className="px-4 py-3 text-xs font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          {l.createdDate ? new Date(l.createdDate).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
                            {l.eventType ?? '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          <div>{l.email ?? '-'}</div>
                          <div className="text-xs text-slate-400">{l.sessionUserId ?? '-'}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{l.role ?? '-'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{l.companyCode ?? '-'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">{l.ipAddress ?? '-'}</td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300 max-w-md truncate" title={l.description ?? ''}>
                          {l.description ?? '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
